import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useTranslation } from "../hooks/useTranslation";
import { AppLogoIcon } from "./icons/AppLogoIcon";
import { DashboardIcon } from "./icons/DashboardIcon";
import { HelpIcon } from "./icons/HelpIcon";
import { InventoryIcon } from "./icons/InventoryIcon";
import { ManualIcon } from "./icons/ManualIcon";
import { PeopleIcon } from "./icons/PeopleIcon";
import { RBLogoIcon } from "./icons/RBLogoIcon";
import { RequestIcon } from "./icons/RequestIcon";
import styles from "./Navbar.module.css";

type NavbarProps = {
  onNavigate?: () => void;
};

type NavItem = {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
};

export function Navbar({ onNavigate }: NavbarProps) {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.isAdmin === true;

  const handleHelp = () => {
    onNavigate?.();
    window.location.href = "mailto:helpdesk@rothoblaas.com?subject=Dev%20Portal%20Support";
  };

  const primaryNavItems: NavItem[] = [
    {
      to: "/",
      label: t("nav.dashboard"),
      icon: (<DashboardIcon className={styles["icon"]} />) as ReactNode,
      end: true,
    },
    {
      to: "/requests",
      label: t("nav.requests"),
      icon: <RequestIcon className={styles["icon"]} />,
    },
    {
      to: "/inventory",
      label: t("nav.inventory"),
      icon: <InventoryIcon className={styles["icon"]} />,
    },
    ...(isAdmin
      ? [
          {
            to: "/users",
            label: t("nav.users"),
            icon: (<PeopleIcon className={styles["icon"]} />) as ReactNode,
          },
        ]
      : []),
  ];

  return (
    <div className={styles["root"]}>
      <div className={styles["brand"]}>
        <AppLogoIcon className={styles["appLogo"]} />
        <div className={styles["brandInfo"]}>
          <RBLogoIcon className={styles["brandPolyline"]} />
          <span className={styles["brandTitle"]}>Dev Portal</span>
        </div>
      </div>

      <nav className={styles["menu"]} aria-label="Primary">
        <ul className={styles["menuList"]}>
          {primaryNavItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                onClick={() => onNavigate?.()}
                className={({ isActive }) => `${styles["menuItem"]} ${isActive ? styles["menuItemActive"] : ""}`}
              >
                {item.icon}
                <span className={styles["label"]}>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className={styles["footer"]}>
        <div className={styles["footerActions"]}>
          <a
            href="/Dev-Portal-Manuale-Utente.pdf"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onNavigate?.()}
            className={styles["actionButton"]}
          >
            <ManualIcon className={styles["icon"]} />
            <span className={styles["label"]}>{t("nav.manual")}</span>
          </a>
          <a
            href="mailto:helpdesk@rothoblaas.com?subject=Dev%20Portal%20Support"
            onClick={handleHelp}
            className={styles["actionButton"]}
          >
            <HelpIcon className={styles["icon"]} />
            <span className={styles["label"]}>{t("nav.help")}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
